import React from "react";
import { Calendar, Database, ExternalLink } from "react-feather";
import {
  KG_INSPECTOR_ICON_DEFAULT_SIZE,
  kgInspectorIcons,
} from "../../kg/canvas/inspector/kgInspectorIcons";
import type { KgInspectorTableOverviewFieldView } from "../../kg/canvas/inspector/types";
import { KeyBadge } from "../../atoms/KeyBadge";
import { KgInspectorDataGrid } from "../KgInspectorDataGrid";
import { KgInspectorDetailRow } from "../KgInspectorDetailRow";
import {
  ColumnsGridWrap,
  ColumnsSection,
  FieldListValue,
  OverviewRoot,
  ShowAllHint,
  SourceLinkButton,
  SourceLogo,
  SourceName,
  SourceValue,
} from "./KgInspectorTableOverview.styles";
import type { KgInspectorTableOverviewProps } from "./KgInspectorTableOverview.types";
import type { KgInspectorTableSourceRef } from "../../kg/canvas/inspector/types";

const ColumnsIcon = kgInspectorIcons.columns;
const columnsIconSize = KG_INSPECTOR_ICON_DEFAULT_SIZE.columns;

const fieldIcon = (field: KgInspectorTableOverviewFieldView) => {
  switch (field.key) {
    case "source":
      return <Database size={16} />;
    default:
      return field.valueType === "date" ? (
        <Calendar size={16} />
      ) : (
        <Database size={16} />
      );
  }
};

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const SourceRefValue: React.FC<{ source: KgInspectorTableSourceRef }> = ({
  source,
}) => (
  <SourceValue>
    {source.logoUrl && <SourceLogo src={source.logoUrl} alt="" aria-hidden />}
    <SourceName>{source.name}</SourceName>
    {source.externalUrl && (
      <SourceLinkButton
        href={source.externalUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open ${source.name} in a new tab`}
        onClick={(event) => event.stopPropagation()}
      >
        <ExternalLink size={14} />
      </SourceLinkButton>
    )}
  </SourceValue>
);

const renderFieldValue = (field: KgInspectorTableOverviewFieldView) => {
  switch (field.valueType) {
    case "ref":
      return (
        <SourceRefValue source={field.value as KgInspectorTableSourceRef} />
      );
    case "list": {
      const items = Array.isArray(field.value) ? field.value : [];
      return (
        <FieldListValue>
          {items.length > 0 ? items.join(", ") : "—"}
        </FieldListValue>
      );
    }
    case "date":
      return formatDate(String(field.value));
    default:
      return String(field.value);
  }
};

export const KgInspectorTableOverview: React.FC<
  KgInspectorTableOverviewProps
> = ({ overview, isColumnsOpen = false, onToggleColumns }) => {
  if (!overview) return null;

  const { fields, tablePreview } = overview;
  const { label, rows, showAllLabel, total } = tablePreview;

  return (
    <OverviewRoot>
      {fields.map((field) => (
        <KgInspectorDetailRow
          key={field.key}
          icon={fieldIcon(field)}
          label={field.label}
          value={renderFieldValue(field)}
        />
      ))}

      <ColumnsSection>
        <KgInspectorDetailRow
          icon={<ColumnsIcon size={columnsIconSize} />}
          label={label}
          badge={total > 0 ? total : undefined}
          onToggle={onToggleColumns}
          isOpen={isColumnsOpen}
        />

        {isColumnsOpen && rows.length > 0 && (
          <ColumnsGridWrap>
            <KgInspectorDataGrid
              rows={rows}
              rowKey={(column) => column.id}
              columns={[
                {
                  key: "name",
                  header: "COLUMN NAME",
                  width: "flex",
                  render: (column) => column.name,
                },
                {
                  key: "type",
                  header: "TYPE",
                  width: "narrow",
                  render: (column) => <KeyBadge kind={column.role} />,
                },
              ]}
            />
            {showAllLabel && total > rows.length && (
              <ShowAllHint>{showAllLabel}</ShowAllHint>
            )}
          </ColumnsGridWrap>
        )}
      </ColumnsSection>
    </OverviewRoot>
  );
};

export default KgInspectorTableOverview;
